import classNames from 'classnames'
import type { FormFieldDefaults } from './types'
import { FormLabel } from './form/FormLabel'
import type { ForwardedRef } from 'react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useId,
} from 'react'
import { FormError } from './form/FormError'

interface BreakoutTextAreaProps extends FormFieldDefaults<'textarea'> {
  // kind secondary is the default and represents the "primary" style in figma
  // tertiary is the secondary style in figma
  kind?: 'secondary' | 'tertiary'
  containerClassName?: string
  inputClassName?: string
  autoGrow?: boolean
  maxLength?: number
  // Counter-only cap: drives the X/N display without setting the HTML
  // maxLength attribute, so users can type past it and validation can flag it.
  softMaxLength?: number
  hideCharacterCount?: boolean
  autoFocusWithDelay?: number
  renderUnderneath?: React.ReactNode
}

export const BreakoutTextArea = forwardRef(function BreakoutTextArea(
  {
    disabled,
    kind = 'tertiary',
    name,
    label,
    labelClass,
    containerClassName,
    inputClassName,
    testId,
    error,
    errorClass,
    required,
    autoGrow,
    maxLength,
    softMaxLength,
    hideCharacterCount,
    value,
    autoFocusWithDelay,
    renderUnderneath,
    ...rest
  }: BreakoutTextAreaProps,
  ref: ForwardedRef<HTMLTextAreaElement>
) {
  const id = useId()
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  const adjustHeight = () => {
    const textarea = textareaRef.current
    if (!textarea) return

    let temporaryPlaceholder = false
    if (textarea.value === '') {
      temporaryPlaceholder = true
      textarea.value = textarea.placeholder
    }
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = '1px'
    // Set the height to the scrollHeight
    textarea.style.height = `${textarea.scrollHeight}px`

    if (temporaryPlaceholder) {
      // Restore the original empty value if it was temporarily set to the placeholder
      textarea.value = ''
    }
  }

  useEffect(() => {
    if (autoFocusWithDelay) {
      const timer = setTimeout(() => {
        textareaRef.current?.focus()
      }, autoFocusWithDelay)
      return () => clearTimeout(timer)
    }
  }, [autoFocusWithDelay])

  const delayedAdjustHeight = useCallback(() => {
    requestAnimationFrame(() => {
      adjustHeight()
    }) // Delay to ensure the DOM is updated
  }, [])

  useEffect(() => {
    if (!autoGrow) return

    // Add event listener for input changes
    const textarea = textareaRef.current

    if (textarea) {
      const form = textarea.closest('form')
      textarea.addEventListener('input', delayedAdjustHeight)
      window.addEventListener('resize', delayedAdjustHeight)
      if (form) form.addEventListener('reset', delayedAdjustHeight)

      // Clean up
      return () => {
        textarea.removeEventListener('input', delayedAdjustHeight)
        window.removeEventListener('resize', delayedAdjustHeight)
        if (form) form.removeEventListener('reset', delayedAdjustHeight)
      }
    }
  }, [autoGrow, delayedAdjustHeight])

  // Also respond to controlled value changes by adjusting height
  useLayoutEffect(() => {
    if (!autoGrow) return
    delayedAdjustHeight()
  }, [value, autoGrow, delayedAdjustHeight])

  return (
    <div
      className={classNames(
        'flex flex-col items-start gap-1',
        containerClassName
      )}
    >
      {label && (
        <FormLabel
          label={label}
          for={id}
          labelClass={labelClass}
          required={required}
        />
      )}

      <textarea
        aria-label={label}
        data-testid={testId ?? (name ? `${name}-textarea` : undefined)}
        ref={(node) => {
          if (typeof ref === 'function') {
            ref(node)
          } else if (ref) {
            ref.current = node
          }
          textareaRef.current = node
        }}
        tabIndex={0}
        name={name}
        id={id}
        disabled={disabled}
        className={classNames(
          'max-h-[250px] min-h-[150px] w-full rounded-2xl p-4 text-label-medium outline outline-1 outline-outline-light [scrollbar-gutter:stable]',
          {
            'bg-core-secondary': kind === 'secondary' && !disabled,
            'bg-core-tertiary': kind === 'tertiary' && !disabled,
            'bg-surface-dim': kind === 'secondary' && disabled,
            'bg-surface': kind === 'tertiary' && disabled,
            'text-on-surface-disabled': disabled,
            // 'focus:ring-1 focus:ring-outline-light': !error,
            // On error the focus ring adopts the error color so the red wins
            // visually while keeping focus visible (WCAG 2.4.7).
            'border border-core-error outline-core-error focus-visible:!outline-core-error':
              !!error,
            'focus-ring-edge': !disabled,
            'cursor-not-allowed': disabled,
          },
          inputClassName
        )}
        maxLength={maxLength}
        value={value}
        {...rest}
      />
      {/* FormError shares a row with the character counter so the counter
          doesn't push the error message down on its own line. */}
      <div className="flex w-full items-start justify-between gap-2">
        <div className="min-w-0 flex-1 text-body-small">
          {renderUnderneath}
          <FormError error={error} errorClass={errorClass} />
        </div>
        {(maxLength ?? softMaxLength) && !hideCharacterCount ? (
          <div className="mt-1 shrink-0 pr-2 text-right text-body-small text-on-surface-var">
            {value?.length || 0}/{softMaxLength ?? maxLength}
          </div>
        ) : null}
      </div>
    </div>
  )
})
